import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import { Check, X, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/appointments`, { withCredentials: true });
      setAppointments(data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/appointments/${id}?status=${status}`, {}, { withCredentials: true });
      toast.success('Statut mis à jour');
      fetchAppointments();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce rendez-vous ?')) return;
    try {
      await axios.delete(`${API}/appointments/${id}`, { withCredentials: true });
      toast.success('Rendez-vous supprimé');
      fetchAppointments();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  return (
    <AdminLayout title="Gestion des Rendez-vous">
      {loading ? (
        <div>Chargement...</div>
      ) : (
        <div className="grid gap-4">
          {appointments.map((appt) => (
            <div key={appt.id} className="bg-white p-4 rounded-lg border" data-testid={`appointment-item-${appt.id}`}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="text-lg font-medium text-[#5C2018]">{appt.name}</h3>
                  <p className="text-sm text-[#332724]">{appt.email} • {appt.phone}</p>
                  <p className="text-sm text-[#B8922A] mt-1">
                    {new Date(appt.date).toLocaleDateString('fr-FR')} à {appt.time} • {appt.service}
                  </p>
                  {appt.notes && <p className="text-xs text-[#332724]/70 mt-2 italic">{appt.notes}</p>}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[appt.status]}`}>
                  {appt.status}
                </span>
              </div>
              <div className="flex gap-2 mt-3">
                {appt.status === 'pending' && (
                  <button
                    onClick={() => updateStatus(appt.id, 'confirmed')}
                    className="p-2 text-green-600 hover:bg-green-50 rounded inline-flex items-center gap-1 text-sm"
                    data-testid={`confirm-${appt.id}`}
                  >
                    <Check size={16} /> Confirmer
                  </button>
                )}
                {appt.status !== 'cancelled' && (
                  <button
                    onClick={() => updateStatus(appt.id, 'cancelled')}
                    className="p-2 text-orange-600 hover:bg-orange-50 rounded inline-flex items-center gap-1 text-sm"
                  >
                    <X size={16} /> Annuler
                  </button>
                )}
                <button
                  onClick={() => handleDelete(appt.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded inline-flex items-center gap-1 text-sm"
                  data-testid={`delete-${appt.id}`}
                >
                  <Trash2 size={16} /> Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminAppointments;
